import * as React from "react";
import { orderBy } from "lodash";
import Avatar from "react-avatar";
import * as OrganizationStore from "../organizations/OrganizationStore";
import * as AccountStore from "../account/AccountStore";
import { Context, SetOrganization } from "../general/ApplicationContext";
import { Organization } from "../users/UserStore";

import { Link, NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const OrganizationContextSwitcher = () => {
    let [filter, setFilter] = React.useState("");
    let [organizationDropdownOpen, setOrganizationDropdownOpen] = React.useState(false);
    let [organizations, setOrganizations] = React.useState([] as Organization[]);
    let [organizationsFiltered, setOrganizationsFiltered] = React.useState([] as Organization[]);
    let [currentOrganization, setCurrentOrganization] = React.useState({} as Organization);
    let [currentUser, setCurrentUser] = React.useState({} as AccountStore.Account);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        OrganizationStore.getOrganizations((response: Organization[]) => {
            if (response && response.length > 0) {
                setOrganizations(response);
                AccountStore.GetCurrentLoggedInUser().then((user) => {
                    setCurrentUser(user);
                    var context = Context();
                    if (context.currentOrganization.id) {
                        selectOrganization(context.currentOrganization as Organization)();
                    } else {
                        if (user.organization) {
                            selectOrganization(response.filter((o) => o.id == user.organization)[0])();
                        } else {
                            selectOrganization(response[0])();
                        }
                    }
                });
            }
        });
    }, []);

    React.useEffect(() => {
        filterOrganizations(filter);
    }, [organizations, filter]);

    // Close dropdown when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOrganizationDropdownOpen(false);
            }
        };

        if (organizationDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [organizationDropdownOpen]);

    const toggleOrganizationDropdown = () => {
        setOrganizationDropdownOpen(!organizationDropdownOpen);
    };

    const organizationSearchOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value;
        setFilter(value);
    };

    const filterOrganizations = (filter?: string) => {
        filter = filter || "";
        try {
            let filterRegex = new RegExp(filter, "i");
            let organizationsFiltered = organizations;
            if (filter) {
                organizationsFiltered = organizations.filter((org) => {
                    let match = false;

                    match = match || filterRegex.test(org.name);

                    return match;
                });
            }
            setOrganizationsFiltered(orderBy(organizationsFiltered, ["name", "asc"]));
        } catch (e) {}
    };

    const selectOrganization = (organization: Organization) => () => {
        setCurrentOrganization(organization);
        SetOrganization(organization);
        setFilter("");
        setOrganizationDropdownOpen(false);
    };

    return (
        <div className="bg-slate-800 px-6 py-4 border-b border-slate-700/50">
            <div className="flex items-center justify-between mx-auto">
                {/* Organization Selector */}
                <div className="flex-1 flex justify-start">
                    {organizations.length == 0 && (
                        <div className="inline-flex items-center px-4 py-2 bg-slate-700/50 text-slate-400 rounded-lg text-sm">
                            <FontAwesomeIcon icon={["fad", "building"]} className="mr-2" />
                            No Organization Selected
                        </div>
                    )}
                    {organizations.length == 1 && (
                        <div className="inline-flex items-center px-5 py-2.5 bg-white/10 text-white rounded-lg text-sm font-medium border border-slate-600/30">
                            <FontAwesomeIcon icon={["fad", "building"]} className="mr-2.5 text-blue-400" />
                            {currentOrganization.name || "No Organization"}
                        </div>
                    )}
                    {organizations.length > 1 && (
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={toggleOrganizationDropdown}
                                className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-white/10 text-white border border-slate-600/30 rounded-lg text-sm font-medium hover:bg-white/15 hover:border-slate-500/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                            >
                                <FontAwesomeIcon icon={["fad", "building"]} className="text-blue-400" />
                                <span className="max-w-xs truncate">{currentOrganization.name || "Select Organization"}</span>
                                <FontAwesomeIcon icon={["fad", "chevron-down"]} className={`text-xs transition-transform duration-200 ${organizationDropdownOpen ? "rotate-180" : ""}`} />
                            </button>

                            {organizationDropdownOpen && (
                                <div className="absolute top-full mt-2 w-80 bg-white border border-slate-200 shadow-2xl rounded-lg overflow-hidden z-50">
                                    {organizations.length > 5 && (
                                        <div className="p-3 bg-slate-50 border-b border-slate-200">
                                            <input
                                                type="text"
                                                name="searchOrganization"
                                                placeholder="Search organizations..."
                                                onChange={organizationSearchOnChange}
                                                value={filter}
                                                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                                                autoFocus
                                            />
                                        </div>
                                    )}
                                    <div className="max-h-72 overflow-y-auto">
                                        {organizationsFiltered.map((org, i) => (
                                            <button
                                                key={`org${i}`}
                                                onClick={selectOrganization(org)}
                                                className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors duration-150 cursor-pointer text-left ${
                                                    currentOrganization.id === org.id ? "bg-blue-50/50" : ""
                                                }`}
                                            >
                                                <FontAwesomeIcon icon={["fad", "building"]} className={`text-xs ${currentOrganization.id === org.id ? "text-blue-500" : "text-slate-400"}`} />
                                                <span className={`font-medium text-sm flex-1 ${currentOrganization.id === org.id ? "text-blue-700" : "text-slate-700"}`}>{org.name}</span>
                                                {currentOrganization.id === org.id && <FontAwesomeIcon icon={["fad", "check"]} className="text-blue-500 text-sm" />}
                                            </button>
                                        ))}
                                    </div>
                                    {organizationsFiltered.length === 0 && <div className="px-4 py-8 text-center text-slate-500 text-sm">No organizations found</div>}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* User Profile Section */}

                <NavLink
                    to="/profile"
                    style={{ textDecoration: "none" }}
                    className="flex items-center space-x-3 rounded-lg hover:bg-gray-800/50 transition-all duration-200 group no-underline"
                >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold text-sm shadow-md">
                        {currentUser.firstName?.[0]}
                        {currentUser.lastName?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-200 truncate group-hover:text-white transition-colors">
                            {currentUser.firstName} {currentUser.lastName}
                        </p>
                        <p className="text-xs text-gray-500 truncate group-hover:text-gray-400 transition-colors">{currentUser.email}</p>
                    </div>
                </NavLink>
            </div>
        </div>
    );
};

export default OrganizationContextSwitcher;
